import React, { Fragment, FunctionComponent, useMemo, useState } from 'react';
import { Field } from 'react-final-form';
import { Typography } from '@mui/material';
import i18next from 'i18next';

import {
  composeValidators,
  digitsOnly,
  emailValidation,
  fileValidation,
  requiredValidation,
  onlyLetters,
  pastDateOnly,
  validateIdCard,
  validateIdCardWithSelection,
} from 'helpers/validations';
import { getDocLabel } from 'helpers/form';
import { getCountries } from 'helpers/countries';
import FieldRequired from 'components/commons/FieldRequired';

import MultiStepForm from 'components/commons/MultiStepForm';
import Input from 'components/inputs/Input';
import SelectInput from 'components/inputs/SelectInput';
import Checkbox from 'components/inputs/Checkbox';
import ProcedureStartStep from 'components/commons/ProcedureStartStep';
import ProcedureReviewStep from 'components/commons/ProcedureReviewStep';
import { IReviewSection } from 'components/commons/ProcedureReviewStep/types';

import { ViewContainer, ViewContent, Title } from 'styles/view';
import { FormRowContainer } from 'styles/form';

import { IJewishnessFormData, IJewishnessFormProps } from './types';
import './i18n';

const JewishnessForm: FunctionComponent<IJewishnessFormProps> = (props: IJewishnessFormProps) => {
  const {
    cementeries,
    civilStates,
    files,
    idCardTypes,
    initialValues,
    isEditing,
    onSubmit,
    tribes,
  } = props;
  const [formValues, setFormValues] = useState<IJewishnessFormData>(initialValues);
  const [showStartStep, setShowStartStep] = useState<boolean>(!isEditing);
  const countries = useMemo(() => getCountries(), []);

  const tabs = [
    i18next.t<string>('JEWISHNESS_FORM:PERSON'),
    ...(formValues.person.civilState === 'MARRIED'
      ? [i18next.t<string>('JEWISHNESS_FORM:SPOUSE')]
      : []),
    i18next.t<string>('JEWISHNESS_FORM:PARENTS'),
    i18next.t<string>('JEWISHNESS_FORM:GRANDPARENTS'),
    i18next.t<string>('JEWISHNESS_FORM:WITNESSES'),
    i18next.t<string>('JEWISHNESS_FORM:COMMENTS'),
  ];

  const today = new Date().toISOString().split('T')[0];

  const requiredFilesDescription = [
    // PESTAÑA: PERSONA (Titulat)
    `[OBLIGATORIO] ${i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE')}: ${i18next.t<string>(
      'PROCEDURES:ID_USER_DESC',
      'DNI (frente y dorso) del solicitante.',
    )}`,
    `[OBLIGATORIO] ${i18next.t<string>(
      'JEWISHNESS_FORM:BIRTH_CERTIFICATE_FILE',
    )}: ${i18next.t<string>(
      'PROCEDURES:BIRTH_CERT_USER_DESC',
      'Partida de nacimiento original del solicitante.',
    )}`,

    // PESTAÑA: PADRES (Tienen FieldRequired en tu código)
    `[OBLIGATORIO] ${i18next.t<string>('JEWISHNESS_FORM:PARENTS_ID_FILES')}: ${i18next.t<string>(
      'PROCEDURES:ID_PARENTS_DESC',
      'DNI (frente y dorso) de ambos padres.',
    )}`,
    `[OBLIGATORIO] ${i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE')}: ${i18next.t<string>(
      'PROCEDURES:KETUBA_PARENTS_DESC',
      'Libreta de matrimonio religioso (Ketubá) de los padres.',
    )}`,
    `[OBLIGATORIO] ${i18next.t<string>(
      'JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE',
    )}: ${i18next.t<string>(
      'PROCEDURES:CERT_PARENTS_DESC',
      'Acta de matrimonio civil de los padres.',
    )}`,

    // PESTAÑA: ABUELOS (Usan Field en tu código, son opcionales)
    `[OPCIONAL] ${i18next.t<string>('JEWISHNESS_FORM:GRANDPARENTS_DOCS')}: ${i18next.t<string>(
      'PROCEDURES:LINEAGE_DESC',
      'Ketubá, Actas de matrimonio o Certificados de entierro de los abuelos.',
    )}`,

    // PESTAÑA: TESTIGOS
    `[OPCIONAL] ${i18next.t<string>('JEWISHNESS_FORM:WITNESS_DOCS')}: ${i18next.t<string>(
      'PROCEDURES:WITNESS_DESC',
      'DNI (frente y dorso) de los testigos designados.',
    )}`,
  ];

  const reviewSections: IReviewSection[] = [
    {
      title: i18next.t('JEWISHNESS_FORM:PERSON_INFORMATION'),
      fields: [
        { label: i18next.t('JEWISHNESS_FORM:LAST_NAME'), value: formValues.person?.lastName },
        { label: i18next.t('JEWISHNESS_FORM:FIRST_NAME'), value: formValues.person?.firstName },
        { label: i18next.t('JEWISHNESS_FORM:ID_CARD_TYPE'), value: formValues.person?.idCardType },
        { label: i18next.t('JEWISHNESS_FORM:ID_CARD'), value: formValues.person?.idCard },
        { label: i18next.t('JEWISHNESS_FORM:COUNTRY'), value: formValues.person?.country },
        {
          label: i18next.t('JEWISHNESS_FORM:PHONE'),
          value: formValues.person?.phone
            ? `${formValues.person.areaCode || ''} ${formValues.person.phone}`
            : null,
        },
        { label: i18next.t('JEWISHNESS_FORM:EMAIL'), value: formValues.person?.email },
        { label: i18next.t('JEWISHNESS_FORM:BIRTHDATE'), value: formValues.person?.birthdate },
        { label: i18next.t('JEWISHNESS_FORM:COMMUNITY'), value: formValues.person?.community },
        { label: i18next.t('JEWISHNESS_FORM:TRIBE'), value: formValues.person?.tribe },
        { label: i18next.t('JEWISHNESS_FORM:CIVIL_STATE'), value: formValues.person?.civilState },
        {
          label: i18next.t('JEWISHNESS_FORM:WEDDING_PLACE'),
          value: formValues.person?.weddingPlace,
        },
      ],
    },
    ...(formValues.person.civilState === 'MARRIED'
      ? [
          {
            title: i18next.t('JEWISHNESS_FORM:SPOUSE_INFORMATION'),
            fields: [
              { label: i18next.t('JEWISHNESS_FORM:LAST_NAME'), value: formValues.spouse?.lastName },
              {
                label: i18next.t('JEWISHNESS_FORM:FIRST_NAME'),
                value: formValues.spouse?.firstName,
              },
              {
                label: i18next.t('JEWISHNESS_FORM:ID_CARD_TYPE'),
                value: formValues.spouse?.idCardType,
              },
              { label: i18next.t('JEWISHNESS_FORM:ID_CARD'), value: formValues.spouse?.idCard },
              { label: i18next.t('JEWISHNESS_FORM:COUNTRY'), value: formValues.spouse?.country },
              {
                label: i18next.t('JEWISHNESS_FORM:BIRTHDATE'),
                value: formValues.spouse?.birthdate,
              },
              { label: i18next.t('JEWISHNESS_FORM:TRIBE'), value: formValues.spouse?.tribe },
              { label: i18next.t('JEWISHNESS_FORM:EMAIL'), value: formValues.spouse?.email },
              {
                label: i18next.t('JEWISHNESS_FORM:COMMUNITY'),
                value: formValues.spouse?.community,
              },
              { label: i18next.t('JEWISHNESS_FORM:ALIVE'), value: formValues.spouse?.alive },
              {
                label: i18next.t('JEWISHNESS_FORM:CEMENTERY'),
                value: formValues.spouse?.cementery,
              },
            ],
          },
        ]
      : []),
    {
      title: i18next.t('JEWISHNESS_FORM:PERSON_PARENTS_INFORMATION'),
      fields: [
        {
          label: i18next.t('JEWISHNESS_FORM:FATHER_LAST_NAME'),
          value: formValues.person.parents?.father?.lastName,
        },
        {
          label: i18next.t('JEWISHNESS_FORM:FATHER_FIRST_NAME'),
          value: formValues.person.parents?.father?.firstName,
        },
        {
          label: i18next.t('JEWISHNESS_FORM:MOTHER_LAST_NAME'),
          value: formValues.person.parents?.mother?.lastName,
        },
        {
          label: i18next.t('JEWISHNESS_FORM:MOTHER_FIRST_NAME'),
          value: formValues.person.parents?.mother?.firstName,
        },
        {
          label: i18next.t('JEWISHNESS_FORM:WEDDING_PLACE'),
          value: formValues.person.parents?.weddingPlace,
        },
        {
          label: i18next.t('JEWISHNESS_FORM:WEDDING_DATE'),
          value: formValues.person.parents?.weddingDate,
        },
        {
          label: `Padre - ${i18next.t('JEWISHNESS_FORM:ID_CARD')}`,
          value: formValues.person.parents?.father?.idCard,
        },
        {
          label: `Madre - ${i18next.t('JEWISHNESS_FORM:ID_CARD')}`,
          value: formValues.person.parents?.mother?.idCard,
        },
      ],
    },
    {
      title: i18next.t('JEWISHNESS_FORM:PERSON_GRANDPARENTS_INFORMATION'),
      fields: [
        {
          label: i18next.t('JEWISHNESS_FORM:PATERNAL_FATHER_LAST_NAME'),
          value: formValues.person.grandParents?.paternalFather?.lastName,
        },
        {
          label: i18next.t('JEWISHNESS_FORM:PATERNAL_FATHER_FIRST_NAME'),
          value: formValues.person.grandParents?.paternalFather?.firstName,
        },
        {
          label: `Paternal - ${i18next.t('JEWISHNESS_FORM:BIRTHPLACE')}`,
          value: formValues.person.grandParents?.paternalFather?.birthplace,
        },

        {
          label: i18next.t('JEWISHNESS_FORM:PATERNAL_MOTHER_LAST_NAME'),
          value: formValues.person.grandParents?.paternalMother?.lastName,
        },
        {
          label: i18next.t('JEWISHNESS_FORM:PATERNAL_MOTHER_FIRST_NAME'),
          value: formValues.person.grandParents?.paternalMother?.firstName,
        },
        {
          label: `Paternal - ${i18next.t('JEWISHNESS_FORM:BIRTHPLACE')}`,
          value: formValues.person.grandParents?.paternalMother?.birthplace,
        },

        {
          label: i18next.t('JEWISHNESS_FORM:MATERNAL_FATHER_LAST_NAME'),
          value: formValues.person.grandParents?.maternalFather?.lastName,
        },
        {
          label: i18next.t('JEWISHNESS_FORM:MATERNAL_FATHER_FIRST_NAME'),
          value: formValues.person.grandParents?.maternalFather?.firstName,
        },
        {
          label: `Maternal - ${i18next.t('JEWISHNESS_FORM:BIRTHPLACE')}`,
          value: formValues.person.grandParents?.maternalFather?.birthplace,
        },

        {
          label: i18next.t('JEWISHNESS_FORM:MATERNAL_MOTHER_LAST_NAME'),
          value: formValues.person.grandParents?.maternalMother?.lastName,
        },
        {
          label: i18next.t('JEWISHNESS_FORM:MATERNAL_MOTHER_FIRST_NAME'),
          value: formValues.person.grandParents?.maternalMother?.firstName,
        },
        {
          label: `Maternal - ${i18next.t('JEWISHNESS_FORM:BIRTHPLACE')}`,
          value: formValues.person.grandParents?.maternalMother?.birthplace,
        },
      ],
    },
    ...(formValues.person?.witnesses || []).map((witness: any, index: number) => ({
      title: `${i18next.t('JEWISHNESS_FORM:PERSON_WITNESSES_INFORMATION')} ${index + 1}`,
      fields: [
        { label: i18next.t('JEWISHNESS_FORM:WITNESS_NAME'), value: witness.name },
        { label: i18next.t('JEWISHNESS_FORM:ID_CARD'), value: witness.idCard },
        { label: i18next.t('JEWISHNESS_FORM:ADDRESS'), value: witness.address },
        {
          label: i18next.t('JEWISHNESS_FORM:PHONE'),
          value: witness.phone ? `${witness.areaCode || ''} ${witness.phone}` : null,
        },
      ],
    })),
    {
      title: i18next.t('JEWISHNESS_FORM:COMMENTS'),
      fields: [{ label: i18next.t('JEWISHNESS_FORM:COMMENTS'), value: formValues.comments }],
    },
  ];

  if (showStartStep) {
    return (
      <ViewContainer auth>
        <ProcedureStartStep
          procedureName={i18next.t<string>('JEWISHNESS_FORM:TITLE')}
          requiredFiles={requiredFilesDescription}
          onStart={() => setShowStartStep(false)}
        />
      </ViewContainer>
    );
  }

  return (
    <ViewContainer auth data-testid="jewishness-form">
      <Title>{i18next.t<string>('JEWISHNESS_FORM:TITLE')}</Title>
      <ViewContent auth>
        <MultiStepForm<IJewishnessFormData>
          initialValues={initialValues}
          onSubmit={onSubmit}
          onValuesChange={setFormValues}
          tabs={tabs}
          tabsEnabled={isEditing}
        >
          <MultiStepForm.Page key="person-page">
            <Typography variant="h6">
              {i18next.t<string>('JEWISHNESS_FORM:PERSON_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:LAST_NAME')}
                name="person.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:FIRST_NAME')}
                name="person.firstName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_TYPE')}
                name="person.idCardType"
                options={idCardTypes}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FieldRequired
                label={getDocLabel('person.idCard', formValues, idCardTypes)}
                name="person.idCard"
                render={Input}
                validate={validateIdCard(idCardTypes)}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE')}
                name="person.idCardFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE'),
                    value: files?.personIdCardFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE')}
                name="person.idCardBackFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE'),
                    value: files?.personIdCardBackFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:COUNTRY')}
                name="person.country"
                options={countries}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:AREA_CODE')}
                  maxLength={4}
                  name="person.areaCode"
                  parse={(v: string) => digitsOnly(v)}
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:PHONE')}
                  maxLength={8}
                  name="person.phone"
                  parse={(v: string) => digitsOnly(v)}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:EMAIL')}
                name="person.email"
                render={Input}
                type="email"
                validate={composeValidators(requiredValidation, emailValidation)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:COMMUNITY')}
                name="person.community"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:BIRTHDATE')}
                name="person.birthdate"
                render={Input}
                type="date"
                max={today}
                validate={composeValidators(requiredValidation, pastDateOnly)}
              />
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:TRIBE')}
                name="person.tribe"
                options={tribes}
                render={SelectInput}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:CIVIL_STATE')}
                name="person.civilState"
                options={civilStates}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:BIRTH_CERTIFICATE_FILE')}
                name="person.birthCertificateFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:BIRTH_CERTIFICATE_FILE'),
                    value: files?.personBirthCertificateFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE')}
                name="person.ketubaFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE'),
                    value: files?.personKetubaFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing || formValues.person.civilState !== 'MARRIED'
                    ? fileValidation
                    : composeValidators(requiredValidation, fileValidation)
                }
              />
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE')}
                name="person.marriageCertificateFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE'),
                    value: files?.personMarriageCertificateFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing || formValues.person.civilState !== 'MARRIED'
                    ? fileValidation
                    : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
            <FormRowContainer>
              {['MARRIED', 'DIVORCED'].includes(`${formValues.person.civilState}`) && (
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:WEDDING_PLACE')}
                  name="person.weddingPlace"
                  render={Input}
                  validate={requiredValidation}
                />
              )}
              {formValues.person.civilState === 'DIVORCED' && (
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:GET_FILE')}
                  name="person.getFile"
                  previewFiles={[
                    {
                      label: i18next.t<string>('JEWISHNESS_FORM:GET_FILE'),
                      value: files?.personGetFile,
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
              )}
            </FormRowContainer>
          </MultiStepForm.Page>
          {formValues.person.civilState === 'MARRIED' && (
            <MultiStepForm.Page key="spouse-page">
              <Typography variant="h6">
                {i18next.t<string>('JEWISHNESS_FORM:SPOUSE_INFORMATION')}
              </Typography>
              <br />
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:LAST_NAME')}
                  name="spouse.lastName"
                  parse={(value: string) => value}
                  render={Input}
                  validate={composeValidators(requiredValidation, onlyLetters)}
                />
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:FIRST_NAME')}
                  name="spouse.firstName"
                  parse={(value: string) => value}
                  render={Input}
                  validate={composeValidators(requiredValidation, onlyLetters)}
                />
              </FormRowContainer>
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_TYPE')}
                  name="spouse.idCardType"
                  options={idCardTypes}
                  parse={(value: string) => value}
                  render={SelectInput}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={getDocLabel('spouse.idCard', formValues, idCardTypes)}
                  name="spouse.idCard"
                  parse={(value: string) => value}
                  render={Input}
                  validate={validateIdCard(idCardTypes)}
                />
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE')}
                  name="spouse.idCardFile"
                  previewFiles={[
                    {
                      label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE'),
                      value: files?.spouseIdCardFile,
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE')}
                  name="spouse.idCardBackFile"
                  previewFiles={[
                    {
                      label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE'),
                      value: files?.spouseIdCardBackFile,
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
              </FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:COUNTRY')}
                name="spouse.country"
                options={countries}
                parse={(value: string) => value}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:BIRTHDATE')}
                  name="spouse.birthdate"
                  parse={(value: string) => value}
                  render={Input}
                  type="date"
                  max={today}
                  validate={composeValidators(requiredValidation, pastDateOnly)}
                />
                <Field
                  label={i18next.t<string>('JEWISHNESS_FORM:TRIBE')}
                  name="spouse.tribe"
                  parse={(value: string) => value}
                  options={tribes}
                  render={SelectInput}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:EMAIL')}
                  name="spouse.email"
                  parse={(value: string) => value}
                  render={Input}
                  type="email"
                  validate={composeValidators(requiredValidation, emailValidation)}
                />
                <FieldRequired
                  label={i18next.t<string>('JEWISHNESS_FORM:COMMUNITY')}
                  name="spouse.community"
                  parse={(value: string) => value}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('JEWISHNESS_FORM:ALIVE')}
                  name="spouse.alive"
                  render={Checkbox}
                />
                {!formValues.spouse.alive && (
                  <Field
                    label={i18next.t<string>('JEWISHNESS_FORM:CEMENTERY')}
                    name="spouse.cementery"
                    options={cementeries}
                    parse={(value: string) => value}
                    render={SelectInput}
                    validate={requiredValidation}
                  />
                )}
              </FormRowContainer>
            </MultiStepForm.Page>
          )}
          <MultiStepForm.Page key="parents-page">
            <Typography variant="h6" sx={{ borderBottom: '2px solid #2563eb', pb: 1, mb: 3 }}>
              {i18next.t<string>('JEWISHNESS_FORM:PERSON_PARENTS_INFORMATION')}
            </Typography>

            {/* --- BLOQUE EXCLUSIVO: PADRE --- */}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 2 }}>
              {i18next.t<string>('MARRIAGES_FORM:FATHER_DATA')}
            </Typography>

            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:FATHER_LAST_NAME')}
                name="person.parents.father.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:FATHER_FIRST_NAME')}
                name="person.parents.father.firstName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>

            <FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_TYPE')}
                name="person.parents.father.idCardType"
                options={idCardTypes}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FieldRequired
                label={getDocLabel('person.parents.father.idCard', formValues, idCardTypes)}
                name="person.parents.father.idCard"
                render={Input}
                validate={validateIdCard(idCardTypes)}
              />
            </FormRowContainer>

            {/* ARCHIVOS ID PADRE */}
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE')}
                name="person.parents.father.idCardFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE'),
                    value: files?.personParentsFatherIdCardFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE')}
                name="person.parents.father.idCardBackFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE'),
                    value: files?.personParentsFatherIdCardBackFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>

            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:BIRTHDATE')}
                name="person.parents.father.birthdate"
                render={Input}
                type="date"
                max={today}
                validate={composeValidators(requiredValidation, pastDateOnly)}
              />
            </FormRowContainer>

            <hr style={{ margin: '30px 0', border: '0', borderTop: '1px solid #eee' }} />

            {/* --- BLOQUE EXCLUSIVO: MADRE --- */}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 2 }}>
              {i18next.t<string>('MARRIAGES_FORM:MOTHER_DATA')}
            </Typography>

            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:MOTHER_LAST_NAME')}
                name="person.parents.mother.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:MOTHER_FIRST_NAME')}
                name="person.parents.mother.firstName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>

            <FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_TYPE')}
                name="person.parents.mother.idCardType"
                options={idCardTypes}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FieldRequired
                label={getDocLabel('person.parents.mother.idCard', formValues, idCardTypes)}
                name="person.parents.mother.idCard"
                render={Input}
                validate={validateIdCard(idCardTypes)}
              />
            </FormRowContainer>

            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE')}
                name="person.parents.mother.idCardFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE'),
                    value: files?.personParentsMotherIdCardFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE')}
                name="person.parents.mother.idCardBackFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE'),
                    value: files?.personParentsMotherIdCardBackFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>

            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:BIRTHDATE')}
                name="person.parents.mother.birthdate"
                render={Input}
                type="date"
                max={today}
                validate={composeValidators(requiredValidation, pastDateOnly)}
              />
            </FormRowContainer>

            <hr style={{ margin: '30px 0', border: '0', borderTop: '1px solid #eee' }} />

            <Typography variant="subtitle2" sx={{ mb: 2, fontStyle: 'italic', color: '#64748b' }}>
              {i18next.t<string>('MARRIAGES_FORM:UNION_DOCS')}
            </Typography>

            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:WEDDING_PLACE')}
                name="person.parents.weddingPlace"
                render={Input}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:WEDDING_DATE')}
                name="person.parents.weddingDate"
                render={Input}
                type="date"
                max={today}
                validate={composeValidators(requiredValidation, pastDateOnly)}
              />
            </FormRowContainer>

            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE')}
                name="person.parents.ketubaFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE'),
                    value: files?.personParentsKetubaFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE')}
                name="person.parents.marriageCertificateFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE'),
                    value: files?.personParentsMarriageCertificateFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
          </MultiStepForm.Page>
          <MultiStepForm.Page key="grand-parents-page">
            <Typography variant="h6">
              {i18next.t<string>('JEWISHNESS_FORM:PERSON_GRANDPARENTS_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:PATERNAL_FATHER_LAST_NAME')}
                name="person.grandParents.paternalFather.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:PATERNAL_FATHER_FIRST_NAME')}
                name="person.grandParents.paternalFather.firstName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FieldRequired
              label={i18next.t<string>('JEWISHNESS_FORM:BIRTHPLACE')}
              name="person.grandParents.paternalFather.birthplace"
              render={Input}
              validate={requiredValidation}
            />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:PATERNAL_MOTHER_LAST_NAME')}
                name="person.grandParents.paternalMother.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:PATERNAL_MOTHER_FIRST_NAME')}
                name="person.grandParents.paternalMother.firstName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FieldRequired
              label={i18next.t<string>('JEWISHNESS_FORM:BIRTHPLACE')}
              name="person.grandParents.paternalMother.birthplace"
              render={Input}
              validate={requiredValidation}
            />
            <FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE')}
                name="person.grandParents.paternalKetubaFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE'),
                    value: files?.personGrandParentsPaternalKetubaFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={fileValidation}
              />
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE')}
                name="person.grandParents.paternalMarriageCertificateFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE'),
                    value: files?.personGrandParentsPaternalMarriageCertificateFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={fileValidation}
              />
            </FormRowContainer>
            <Field
              label={i18next.t<string>('JEWISHNESS_FORM:CEMENTERY_BURIAL_CERTIFICATE_FILE')}
              name="person.grandParents.paternalCemeteryBurialCertificateFile"
              previewFiles={[
                {
                  label: i18next.t<string>('JEWISHNESS_FORM:CEMENTERY_BURIAL_CERTIFICATE_FILE'),
                  value: files?.personGrandParentsPaternalCemeteryBurialCertificateFile,
                },
              ]}
              render={Input}
              type="file"
              validate={fileValidation}
            />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:MATERNAL_FATHER_LAST_NAME')}
                name="person.grandParents.maternalFather.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:MATERNAL_FATHER_FIRST_NAME')}
                name="person.grandParents.maternalFather.firstName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FieldRequired
              label={i18next.t<string>('JEWISHNESS_FORM:BIRTHPLACE')}
              name="person.grandParents.maternalFather.birthplace"
              render={Input}
              validate={requiredValidation}
            />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:MATERNAL_MOTHER_LAST_NAME')}
                name="person.grandParents.maternalMother.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('JEWISHNESS_FORM:MATERNAL_MOTHER_FIRST_NAME')}
                name="person.grandParents.maternalMother.firstName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FieldRequired
              label={i18next.t<string>('JEWISHNESS_FORM:BIRTHPLACE')}
              name="person.grandParents.maternalMother.birthplace"
              render={Input}
              validate={requiredValidation}
            />
            <FormRowContainer>
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE')}
                name="person.grandParents.maternalKetubaFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE'),
                    value: files?.personGrandParentsMaternalKetubaFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={fileValidation}
              />
              <Field
                label={i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE')}
                name="person.grandParents.maternalMarriageCertificateFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE'),
                    value: files?.personGrandParentsMaternalMarriageCertificateFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={fileValidation}
              />
            </FormRowContainer>
            <Field
              label={i18next.t<string>('JEWISHNESS_FORM:CEMENTERY_BURIAL_CERTIFICATE_FILE')}
              name="person.grandParents.maternalCemeteryBurialCertificateFile"
              previewFiles={[
                {
                  label: i18next.t<string>('JEWISHNESS_FORM:CEMENTERY_BURIAL_CERTIFICATE_FILE'),
                  value: files?.personGrandParentsMaternalCemeteryBurialCertificateFile,
                },
              ]}
              render={Input}
              type="file"
              validate={fileValidation}
            />
          </MultiStepForm.Page>
          <MultiStepForm.Page key="witnesses-page">
            <Typography variant="h6">
              {i18next.t<string>('JEWISHNESS_FORM:PERSON_WITNESSES_INFORMATION')}
            </Typography>
            <br />
            {(formValues.person?.witnesses as Array<unknown>)?.map((_witness: any, i: number) => {
              const currentTypeId = _witness?.idCardType;
              const selectedType = idCardTypes.find((t) => t.value === currentTypeId);

              const dynamicLabel = selectedType
                ? selectedType.label
                : i18next.t<string>('JEWISHNESS_FORM:ID_CARD');

              return (
                <Fragment key={`witness-${i.toString()}`}>
                  <FieldRequired
                    label={`${i18next.t<string>('JEWISHNESS_FORM:WITNESS_NAME')} ${i + 1}`}
                    name={`person.witnesses[${i}].name`}
                    parse={(v: string) => v}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                  <FormRowContainer>
                    <Field
                      label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_TYPE')}
                      name={`person.witnesses[${i}].idCardType`}
                      options={idCardTypes}
                      parse={(v) => v}
                      render={SelectInput}
                    />
                    <Field
                      label={dynamicLabel}
                      name={`person.witnesses[${i}].idCard`}
                      parse={(v) => v}
                      render={Input}
                      validate={validateIdCardWithSelection(idCardTypes, currentTypeId, false)}
                    />
                  </FormRowContainer>
                  <FormRowContainer>
                    <Field
                      label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE')}
                      name={`person.witnesses[${i}].idCardFile`}
                      previewFiles={[
                        {
                          label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_FILE'),
                          value: files?.personWitnessesIdCardFiles?.[i],
                        },
                      ]}
                      render={Input}
                      type="file"
                      validate={fileValidation}
                    />
                    <Field
                      label={i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE')}
                      name={`person.witnesses[${i}].idCardBackFile`}
                      previewFiles={[
                        {
                          label: i18next.t<string>('JEWISHNESS_FORM:ID_CARD_BACK_FILE'),
                          value: files?.personWitnessesIdCardBackFiles?.[i],
                        },
                      ]}
                      render={Input}
                      type="file"
                      validate={fileValidation}
                    />
                  </FormRowContainer>
                  <FormRowContainer>
                    <Field
                      label={i18next.t<string>('JEWISHNESS_FORM:ADDRESS')}
                      name={`person.witnesses[${i}].address`}
                      parse={(v) => v}
                      render={Input}
                    />
                    <FormRowContainer>
                      <Field
                        label={i18next.t<string>('JEWISHNESS_FORM:AREA_CODE')}
                        maxLength={4}
                        name={`person.witnesses[${i}].areaCode`}
                        parse={(v) => digitsOnly(v)}
                        render={Input}
                      />
                      <Field
                        label={i18next.t<string>('JEWISHNESS_FORM:PHONE')}
                        maxLength={8}
                        name={`person.witnesses[${i}].phone`}
                        parse={(v) => digitsOnly(v)}
                        render={Input}
                      />
                    </FormRowContainer>
                  </FormRowContainer>
                  <FormRowContainer>
                    <Field
                      label={i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE')}
                      name={`person.witnesses[${i}].ketubaFile`}
                      previewFiles={[
                        {
                          label: i18next.t<string>('JEWISHNESS_FORM:KETUBA_FILE'),
                          value: files?.personWitnessesKetubaFiles?.[i],
                        },
                      ]}
                      render={Input}
                      type="file"
                      validate={fileValidation}
                    />
                    <Field
                      label={i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE')}
                      name={`person.witnesses[${i}].marriageCertificateFile`}
                      previewFiles={[
                        {
                          label: i18next.t<string>('JEWISHNESS_FORM:MARRIAGE_CERTIFICATE_FILE'),
                          value: files?.personWitnessesMarriageCertificateFiles?.[i],
                        },
                      ]}
                      render={Input}
                      type="file"
                      validate={fileValidation}
                    />
                  </FormRowContainer>
                </Fragment>
              );
            })}
          </MultiStepForm.Page>
          <MultiStepForm.Page key="comments-page">
            <Field
              label={i18next.t<string>('JEWISHNESS_FORM:COMMENTS')}
              name="comments"
              parse={(value) => value}
              render={Input}
              type="textarea"
            />
          </MultiStepForm.Page>
          {!isEditing && (
            <MultiStepForm.Page key="review-page">
              <ProcedureReviewStep sections={reviewSections} />
            </MultiStepForm.Page>
          )}
        </MultiStepForm>
      </ViewContent>
    </ViewContainer>
  );
};

export default JewishnessForm;
